<script setup lang="ts">
import { ref } from 'vue';

import { AVATAR_SETS, isKnownAvatar } from './avatar-catalog';

/** 头像选择：按套分页签，点选即保存；current 为空表示正在使用默认头像。 */
const props = defineProps<{ busy?: boolean; current?: null | string; error?: string }>();
const emit = defineEmits<{ pick: [avatar: string] }>();

const activeSet = ref(AVATAR_SETS.find((set) => set.items.some((item) => item.id === props.current))?.id ?? AVATAR_SETS[0]!.id);
</script>

<template>
  <div class="avatar-picker" data-testid="avatar-picker">
    <div class="avatar-picker__tabs" role="tablist" aria-label="头像分类">
      <button
        v-for="set in AVATAR_SETS"
        :key="set.id"
        type="button"
        role="tab"
        :aria-selected="activeSet === set.id"
        @click="activeSet = set.id"
      >{{ set.label }}</button>
    </div>
    <template v-for="set in AVATAR_SETS" :key="set.id">
      <div v-if="activeSet === set.id" class="avatar-picker__grid" role="radiogroup" :aria-label="`${set.label}头像`">
        <button
          v-for="item in set.items"
          :key="item.id"
          type="button"
          role="radio"
          class="avatar-picker__item"
          :aria-checked="current === item.id"
          :aria-label="item.id"
          :disabled="busy"
          :data-avatar="item.id"
          data-testid="avatar-picker-item"
          @click="emit('pick', item.id)"
        ><img :src="item.url" alt="" draggable="false" /></button>
      </div>
    </template>
    <div class="avatar-picker__foot">
      <button v-if="isKnownAvatar(current)" type="button" class="avatar-picker__reset" :disabled="busy" @click="emit('pick', '')">恢复默认</button>
      <span v-if="error" class="avatar-picker__error" role="alert">{{ error }}</span>
    </div>
  </div>
</template>

<style scoped>
.avatar-picker { display: grid; gap: 10px; width: 272px; }
.avatar-picker__tabs { display: flex; gap: 4px; }
.avatar-picker__tabs button {
  flex: 1;
  min-height: 28px;
  padding: 0 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  box-shadow: none;
  color: #646a73;
  font-size: 13px;
  cursor: pointer;
}
.avatar-picker__tabs button[aria-selected='true'] { background: #ecf7f9; color: #0b7182; font-weight: 600; }
.avatar-picker__grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
.avatar-picker__item {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 50%;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
}
.avatar-picker__item img { width: 100%; height: 100%; border-radius: 50%; }
.avatar-picker__item:hover:not(:disabled) { border-color: #b8dfe5; }
.avatar-picker__item[aria-checked='true'] { border-color: #17a2b8; }
.avatar-picker__item:disabled { cursor: progress; opacity: 0.6; }
.avatar-picker__foot { display: flex; align-items: center; gap: 8px; min-height: 20px; }
.avatar-picker__reset {
  padding: 0;
  border: 0;
  background: none;
  box-shadow: none;
  color: #0b7182;
  font-size: 12px;
  cursor: pointer;
}
.avatar-picker__error { color: #d4380d; font-size: 12px; }
</style>
